import { Story } from '../../dorian-shared/types/story/Story';
import { User } from '../../dorian-shared/types/user/User';

/**
 * Basil's write gate.
 *
 * Visibility (who sees the sidebar item) is `isBasilEnabled` in
 * `components/utils/userActionUtils.ts` and it deliberately has an admin path.
 * Authorization to APPLY is this file, and it deliberately has none: the two
 * gates are separate on purpose, and neither one is a server-side entitlement.
 *
 * The gate is environment-aware (NF amendment A1). The team tests on the dev
 * namespace and must be able to apply there, so:
 *
 * - on `'live'`: only the owner, only an allow-listed story, only a draft;
 * - on `'test'`: any authenticated user, any draft that is not denylisted;
 * - in BOTH: the story must exist, must not be in `BASIL_LIVE_DENYLIST`, and
 *   `isStoryDraft(story)` must hold.
 *
 * An unrecognised, empty or malformed engine base URL reads as `'live'` —
 * the strict environment — so a detection failure fails closed.
 */

/** Owner user id allowed to write with Basil on the live namespace (ruling D4). */
export const BASIL_WRITE_USER_ID = 4219637;

/** Story ids Basil may write to on the live namespace (ruling D4). */
export const BASIL_WRITE_STORY_ALLOWLIST: readonly number[] = [475513];

/** Live story ids that are refused in every environment, whatever their group says. */
export const BASIL_LIVE_DENYLIST: readonly number[] = [
  459498, 462299, 458408, 458416, 458425, 458442, 458464, 458475, 458486, 458499,
];

/**
 * Which engine namespace the page is talking to.
 *
 * `'live'` is the production engine; `'test'` is every other namespace (dev,
 * staging, a feature namespace, a local editor-api, localhost).
 */
export type BasilEnvironment = 'live' | 'test';

/**
 * The production engine hostnames, established from `components/api.js`:
 *
 * - `api.js:7`  `const LIVE_ROOT_DOMAIN = 'live.4dorian.net';`
 * - `api.js:8`  `const AUX_LIVE_DOMAINS = ['dorian.live'];`
 * - `api.js:11` `const LIVE_DOMAINS = [LIVE_ROOT_DOMAIN, ...AUX_LIVE_DOMAINS];`
 * - `api.js:16` `porpoiseProductionPath = (sub) => `https://${sub}.${LIVE_ROOT_DOMAIN}``
 * - `api.js:29-31` when `window.location.hostname` ends with any `LIVE_DOMAINS`
 *   entry, `getPorpoiseSubdomain('engine')` returns the production path
 * - `api.js:38` `export const baseURL = `${getPorpoiseSubdomain('engine')}/``
 *
 * So the live engine base URL the `api` instance targets is
 * `https://engine.live.4dorian.net/`. The bare production host
 * (`live.4dorian.net`) and the auxiliary live domain (`dorian.live`) are
 * matched too, so that a hosts-mapped live hostname (ruling D3) reads `'live'`
 * however the deployment spells it.
 */
export const BASIL_LIVE_ENGINE_HOST_SUFFIXES: readonly string[] = [
  'live.4dorian.net',
  'dorian.live',
];

/**
 * The non-production namespace root, established from `components/api.js`:
 *
 * - `api.js:6`  `const ROOT_DOMAIN = 'test.4dorian.net';`
 * - `api.js:13` the namespace matcher `^\w+\.(\w[\w-]*\w)\.test\.4dorian\.net$`
 * - `api.js:15` `porpoisePath = (sub, namespace) => `https://${sub}.${namespace}.${ROOT_DOMAIN}``
 * - `api.js:33-35` an unmatched host falls back to `DEFAULT_PORPOISE_NAMESPACE = 'dev'` (`api.js:9`)
 * - `api.js:21-27` `REACT_APP_USE_EDITOR_API_URL === 'true'` points the engine at
 *   `REACT_APP_EDITOR_API_URL` (default `http://localhost:4000`)
 */
export const BASIL_TEST_ENGINE_HOST_SUFFIXES: readonly string[] = [
  'test.4dorian.net',
  'localhost',
  '127.0.0.1',
  '[::1]',
];

/**
 * Lowercased hostname of an engine base URL, without scheme, credentials, port,
 * path, query or fragment. Deliberately string surgery rather than `new URL()`:
 * the value may be a bare host, may be relative, and must never throw.
 *
 * Returns `''` when nothing host-shaped can be recovered.
 */
export function basilEngineHostname(engineBaseUrl?: string | null): string {
  if (typeof engineBaseUrl !== 'string') {
    return '';
  }

  const trimmed = engineBaseUrl.trim();

  if (trimmed === '') {
    return '';
  }

  const withoutScheme = trimmed.replace(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//, '');
  const authority = withoutScheme.split(/[/?#]/)[0];
  const host = authority.split('@').pop() || '';
  // Strip the port, but keep a bracketed IPv6 literal intact.
  const withoutPort = host.startsWith('[')
    ? (host.match(/^\[[^\]]*\]/) || [''])[0]
    : host.split(':')[0];

  return withoutPort.toLowerCase();
}

function hostMatches(hostname: string, suffixes: readonly string[]): boolean {
  return suffixes.some((suffix) => hostname === suffix || hostname.endsWith(`.${suffix}`));
}

/**
 * Which namespace an engine base URL points at.
 *
 * Pass `api.defaults.baseURL` from `components/api.js`. Live hosts are matched
 * first, then the known test hosts; anything unrecognised (including `''` and
 * `undefined`) reads `'live'`, which is the strict environment — detection
 * failure must never widen the gate (negative test E25).
 */
export function detectBasilEnvironment(engineBaseUrl?: string | null): BasilEnvironment {
  const hostname = basilEngineHostname(engineBaseUrl);

  if (hostname === '') {
    return 'live';
  }

  if (hostMatches(hostname, BASIL_LIVE_ENGINE_HOST_SUFFIXES)) {
    return 'live';
  }

  if (hostMatches(hostname, BASIL_TEST_ENGINE_HOST_SUFFIXES)) {
    return 'test';
  }

  return 'live';
}

/**
 * Draft = NOT live. The Story Branches page's only liveness test is
 * `isLiveEditDisabled` (`StoryBranches/index.js:176-180`): read-only when
 * `story.group` is `'live'` or `'liveprompt'`. There is no status field and no
 * `isDraft` flag on `Story` (`Story.ts:42-81`).
 */
export function isStoryDraft(story?: Pick<Story, 'group'> | null): boolean {
  if (!story) {
    return false;
  }

  return !['live', 'liveprompt'].includes(story.group);
}

/**
 * Ruling D4 as amended by A1: no admin fallback in any environment.
 *
 * This function must NOT call `isUserActionEnabled` — that returns `true` for
 * every admin before examining actions (`userActionUtils.ts:9-18`), which is
 * the admin bypass the reconciliation confirmed.
 *
 * @param user the authenticated user; `null`/`undefined` is always refused
 * @param story the episode being written to
 * @param environment from {@link detectBasilEnvironment}; omitted reads `'live'`
 */
export function canBasilWrite(
  user: Pick<User, 'id'> | null | undefined,
  story: Pick<Story, 'id' | 'group'> | null | undefined,
  environment: BasilEnvironment = 'live',
): boolean {
  if (!user || !story) {
    return false;
  }

  if (BASIL_LIVE_DENYLIST.includes(story.id)) {
    return false;
  }

  if (!isStoryDraft(story)) {
    return false;
  }

  if (environment === 'live') {
    if (user.id !== BASIL_WRITE_USER_ID) {
      return false;
    }

    if (!BASIL_WRITE_STORY_ALLOWLIST.includes(story.id)) {
      return false;
    }
  }

  return true;
}
